import React, { useState } from 'react';
import { motion } from "framer-motion";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const states = [
  "Alabama", "Alaska", "Arizona", "Arkansas", "California", "Colorado", "Connecticut", 
  "Delaware", "Florida", "Georgia", "Hawaii", "Idaho", "Illinois", "Indiana", "Iowa", 
  "Kansas", "Kentucky", "Louisiana", "Maine", "Maryland", "Massachusetts", "Michigan", 
  "Minnesota", "Mississippi", "Missouri", "Montana", "Nebraska", "Nevada", "New Hampshire", 
  "New Jersey", "New Mexico", "New York", "North Carolina", "North Dakota", "Ohio", 
  "Oklahoma", "Oregon", "Pennsylvania", "Rhode Island", "South Carolina", "South Dakota", 
  "Tennessee", "Texas", "Utah", "Vermont", "Virginia", "Washington", "West Virginia", 
  "Wisconsin", "Wyoming"
];

const BusinessAddressStep = ({ onNext, initialValue = {
  streetAddress: '',
  addressLine2: '',
  city: '',
  state: '',
  zipCode: ''
} }) => {
  const [formData, setFormData] = useState(initialValue);
  const [errors, setErrors] = useState({});

  const validateForm = () => {
    const newErrors = {};
    if (!formData.streetAddress.trim()) {
      newErrors.streetAddress = 'Street address is required';
    }
    if (!formData.city.trim()) {
      newErrors.city = 'City is required';
    }
    if (!formData.state) {
      newErrors.state = 'State is required';
    }
    if (!formData.zipCode.trim()) {
      newErrors.zipCode = 'ZIP code is required';
    } else if (!/^\d{5}(-\d{4})?$/.test(formData.zipCode)) {
      newErrors.zipCode = 'Please enter a valid ZIP code';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      onNext(formData);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: undefined
      }));
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="flex flex-col min-h-[400px] md:min-h-[450px]"
    >
      <div className="space-y-3 mb-6">
        <div className="text-center space-y-1">
          <motion.h2
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-2xl md:text-3xl font-bold text-gray-900"
          >
            Where is your business located?
          </motion.h2>
          <div className="w-12 h-0.5 bg-[#1a4a1a] mx-auto"></div>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="text-gray-500 text-sm md:text-base mt-2"
          >
            Please provide your business address details
          </motion.p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="flex-grow space-y-6">
        <div className="space-y-2">
          <Label htmlFor="streetAddress" className="text-base text-gray-700">Street Address</Label>
          <Input
            id="streetAddress"
            placeholder="123 Business Street"
            value={formData.streetAddress}
            onChange={(e) => handleChange('streetAddress', e.target.value)}
            className={`w-full text-base md:text-lg py-6 ${errors.streetAddress ? 'border-red-500' : ''}`}
          />
          {errors.streetAddress && (
            <p className="text-red-500 text-sm">{errors.streetAddress}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="addressLine2" className="text-base text-gray-700">Address Line 2 (Optional)</Label>
          <Input
            id="addressLine2"
            placeholder="Apt 4B, Floor 2"
            value={formData.addressLine2}
            onChange={(e) => handleChange('addressLine2', e.target.value)}
            className="w-full text-base md:text-lg py-6"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="city" className="text-base text-gray-700">City</Label>
          <Input
            id="city"
            placeholder="Enter your city"
            value={formData.city}
            onChange={(e) => handleChange('city', e.target.value)}
            className={`w-full text-base md:text-lg py-6 ${errors.city ? 'border-red-500' : ''}`}
          />
          {errors.city && (
            <p className="text-red-500 text-sm">{errors.city}</p>
          )}
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="state" className="text-base text-gray-700">State</Label>
            <Select
              value={formData.state}
              onValueChange={(value) => handleChange('state', value)}
            >
              <SelectTrigger className={`w-full text-base md:text-lg py-6 ${errors.state ? 'border-red-500' : ''}`}>
                <SelectValue placeholder="Select state" />
              </SelectTrigger>
              <SelectContent>
                {states.map((state) => (
                  <SelectItem key={state} value={state} className="text-base cursor-pointer py-3 px-4">
                    {state}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.state && (
              <p className="text-red-500 text-sm">{errors.state}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="zipCode" className="text-base text-gray-700">ZIP Code</Label>
            <Input
              id="zipCode"
              placeholder="Enter ZIP code"
              value={formData.zipCode}
              onChange={(e) => handleChange('zipCode', e.target.value)}
              className={`w-full text-base md:text-lg py-6 ${errors.zipCode ? 'border-red-500' : ''}`}
              maxLength={10}
            />
            {errors.zipCode && (
              <p className="text-red-500 text-sm">{errors.zipCode}</p>
            )}
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="mt-6"
        >
          <Button
            type="submit"
            className="w-full bg-[#1a4a1a] hover:bg-[#1a4a1a]/90 text-white py-3 md:py-4 text-base md:text-lg font-semibold rounded-lg shadow-md hover:shadow-lg transition-all"
          >
            Continue
          </Button>
        </motion.div>
      </form>
    </motion.div>
  );
};

export default BusinessAddressStep; 